import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { clampFloatingWindowOffset } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/floatingWindowOffset'
import { GripHorizontal, PanelRight } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

const PRODUCT_NAME = 'Cantilever'

const WindowRoot = styled.div.attrs<{ $x: number; $y: number }>(
  ({ $x, $y }) => ({
    style: { transform: `translate(${$x}px, ${$y}px)` },
  })
)<{ $x: number; $y: number }>`
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  z-index: 30;
  display: flex;
  width: min(26rem, calc(100% - 2rem));
  height: min(75%, 42rem);
  min-height: 18rem;
  flex-direction: column;
  overflow: hidden;
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border2};
  border-radius: 0.5rem;
  background: var(--card);
  box-shadow: 0 0.75rem 2rem rgb(0 0 0 / 0.25);
`

const WindowHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-bottom: 0.0625rem solid ${({ theme }) => theme.forecast.border2};
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`

const HeaderSpacer = styled.div`
  flex: 1;
`

const WindowBody = styled.div`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
`

function isHeaderControl(target: EventTarget): boolean {
  return target instanceof Element && target.closest('button') !== null
}

interface DragState {
  pointerId: number
  startClientX: number
  startClientY: number
  baseX: number
  baseY: number
}

/** The undocked chat: a window that can be dragged around its container. */
export function FloatingChatWindow({
  onDock,
  children,
}: {
  onDock: () => void
  children: React.ReactNode
}): React.ReactElement {
  const { t } = useLingui()
  const windowRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const [offset, setOffset] = useState({ x: 0, y: 0 })

  const handleHeaderPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0 || isHeaderControl(event.target)) return
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      dragRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        baseX: offset.x,
        baseY: offset.y,
      }
    },
    [offset]
  )

  const handleHeaderPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      const windowEl = windowRef.current
      const container = windowEl?.offsetParent
      if (
        !drag ||
        drag.pointerId !== event.pointerId ||
        !windowEl ||
        !container
      ) {
        return
      }
      setOffset(
        clampFloatingWindowOffset({
          candidate: {
            x: drag.baseX + event.clientX - drag.startClientX,
            y: drag.baseY + event.clientY - drag.startClientY,
          },
          containerRect: container.getBoundingClientRect(),
          current: offset,
          windowRect: windowEl.getBoundingClientRect(),
        })
      )
    },
    [offset]
  )

  const handleHeaderPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (dragRef.current?.pointerId !== event.pointerId) return
      dragRef.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    },
    []
  )

  return (
    <WindowRoot
      ref={windowRef}
      role='dialog'
      aria-label={t`Floating chat`}
      $x={offset.x}
      $y={offset.y}>
      <WindowHeader
        onPointerDown={handleHeaderPointerDown}
        onPointerMove={handleHeaderPointerMove}
        onPointerUp={handleHeaderPointerUp}
        onPointerCancel={handleHeaderPointerUp}>
        <GripHorizontal size={14} aria-hidden='true' />
        <Text as='span' variant='caption' color='muted'>
          {PRODUCT_NAME}
        </Text>
        <HeaderSpacer />
        <Button
          variant='ghost'
          size='icon-sm'
          type='button'
          aria-label={t`Dock the chat beside the preview`}
          title={t`Dock the chat beside the preview`}
          onClick={onDock}>
          <PanelRight size={14} />
        </Button>
      </WindowHeader>
      <WindowBody>{children}</WindowBody>
    </WindowRoot>
  )
}
